import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { appAPI, statsAPI, settingsAPI, authAPI, ratingAPI, contactAPI } from '../services/api';

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [settings, setSettings] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentView, setCurrentView] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingApp, setEditingApp] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Closed by default on mobile
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    app_name: '',
    package_name: '',
    version: '',
    version_code: '',
    description: '',
    long_description: '',
    category: 'Tools',
    size_mb: '',
    min_android_version: 'Android 6.0+',
    whats_new: ''
  });
  const [apkFile, setApkFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [screenshotPreviews, setScreenshotPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    
    // Get base URL without /api suffix
    const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'https://apibackend.sisateck.com';
    
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  const handleScreenshotChange = (e) => {
    const files = Array.from(e.target.files);
    setScreenshots(files);
    
    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    setScreenshotPreviews(previews);
  };

  const removeScreenshot = (index) => {
    const newScreenshots = screenshots.filter((_, i) => i !== index);
    const newPreviews = screenshotPreviews.filter((_, i) => i !== index);
    
    // Revoke the removed preview URL to free memory
    URL.revokeObjectURL(screenshotPreviews[index]);
    
    setScreenshots(newScreenshots);
    setScreenshotPreviews(newPreviews);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await authAPI.verifyToken(token);
      setIsAuthenticated(true);
      fetchStats();
      fetchApps();
      fetchSettings();
      fetchMessages();
      fetchUnreadCount();
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await statsAPI.getOverview();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchApps = async () => {
    try {
      const response = await appAPI.getAllApps();
      setApps(response.data);
    } catch (error) {
      console.error('Error fetching apps:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await settingsAPI.getAllSettings(token);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await contactAPI.getAllMessages();
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await contactAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await contactAPI.markAsRead(id);
      fetchMessages();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await contactAPI.deleteMessage(id);
      fetchMessages();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const fetchAllRatings = async () => {
    try {
      const allRatings = [];
      for (const app of apps) {
        const response = await ratingAPI.getRatings(app.id, 1, 100);
        const ratingsWithAppName = response.data.ratings.map(r => ({
          ...r,
          app_name: app.app_name
        }));
        allRatings.push(...ratingsWithAppName);
      }
      setRatings(allRatings);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    setFormData({
      app_name: app.app_name,
      package_name: app.package_name,
      version: app.version,
      version_code: app.version_code,
      description: app.description,
      long_description: app.long_description,
      category: app.category,
      size_mb: app.size_mb,
      min_android_version: app.min_android_version,
      whats_new: app.whats_new || ''
    });
    setApkFile(null);
    setIconFile(null);
    setScreenshots([]);
    setCurrentView('edit');
  };

  const handleUpdateApp = async (e) => {
    e.preventDefault();
    if (!editingApp) return;

    try {
      const token = localStorage.getItem('admin_token');
      const formDataToSend = new FormData();

      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (apkFile) formDataToSend.append('apk', apkFile);
      if (iconFile) formDataToSend.append('icon', iconFile);
      if (screenshots.length > 0) {
        screenshots.forEach(file => {
          formDataToSend.append('screenshots', file);
        });
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/apps/${editingApp.id}/update-with-files`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) throw new Error('Update failed');

      fetchApps();
      fetchStats();
      setEditingApp(null);
      setCurrentView('apps');
      alert('App updated successfully');
    } catch (error) {
      console.error('Error updating app:', error);
      alert('Failed to update app');
    }
  };

  const handleApproveApp = async (appId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await appAPI.approveApp(token, appId);
      fetchApps();
      alert('App approved successfully');
    } catch (error) {
      console.error('Error approving app:', error);
      alert('Failed to approve app');
    }
  };

  const handleRejectApp = async (appId) => {
    if (!window.confirm('Are you sure you want to reject this app?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await appAPI.rejectApp(token, appId);
      fetchApps();
      alert('App rejected');
    } catch (error) {
      console.error('Error rejecting app:', error);
      alert('Failed to reject app');
    }
  };

  const handleDeleteApp = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this app? All associated data will be removed.')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await appAPI.deleteApp(token, appId);
      fetchApps();
      fetchStats();
      alert('App deleted successfully');
    } catch (error) {
      console.error('Error deleting app:', error);
      alert('Failed to delete app');
    }
  };

  const handleReplyToRating = async (ratingId, reply) => {
    try {
      const token = localStorage.getItem('admin_token');
      await ratingAPI.replyToRating(token, ratingId, reply);
      fetchAllRatings();
      alert('Reply added successfully');
    } catch (error) {
      console.error('Error replying to rating:', error);
      alert('Failed to add reply');
    }
  };

  const handleDeleteRating = async (ratingId) => {
    if (!window.confirm('Are you sure you want to delete this rating?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await ratingAPI.deleteRating(token, ratingId);
      fetchAllRatings();
      alert('Rating deleted successfully');
    } catch (error) {
      console.error('Error deleting rating:', error);
      alert('Failed to delete rating');
    }
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      const token = localStorage.getItem('admin_token');
      await settingsAPI.updateSetting(token, key, value);
      fetchSettings();
      alert('Setting updated successfully');
    } catch (error) {
      console.error('Error updating setting:', error);
      alert('Failed to update setting');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!apkFile || !iconFile) {
      alert('Please select both APK file and app icon');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Stage 1: Validate files
      setUploadStage('Validating files...');
      setUploadProgress(10);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Stage 2: Upload APK
      setUploadStage('Uploading APK file...');
      setUploadProgress(25);
      
      const apkFormData = new FormData();
      apkFormData.append('apk', apkFile);
      
      const apkResponse = await fetch(`${process.env.REACT_APP_API_URL}/upload/apk`, {
        method: 'POST',
        body: apkFormData
      });
      
      if (!apkResponse.ok) throw new Error('APK upload failed');
      const apkData = await apkResponse.json();
      setUploadProgress(40);

      // Stage 3: Upload Icon
      setUploadStage('Uploading app icon...');
      const iconFormData = new FormData();
      iconFormData.append('icon', iconFile);
      
      const iconResponse = await fetch(`${process.env.REACT_APP_API_URL}/upload/icon`, {
        method: 'POST',
        body: iconFormData
      });
      
      if (!iconResponse.ok) throw new Error('Icon upload failed');
      const iconData = await iconResponse.json();
      setUploadProgress(60);

      // Stage 4: Create App Entry
      setUploadStage('Creating app entry...');
      const appPayload = {
        ...formData,
        apk_url: apkData.data.url,
        icon_url: iconData.data.url
      };

      const createResponse = await fetch(`${process.env.REACT_APP_API_URL}/apps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(appPayload)
      });

      if (!createResponse.ok) throw new Error('App creation failed');
      const result = await createResponse.json();
      setUploadProgress(80);

      // Stage 5: Upload Screenshots (if any)
      if (screenshots.length > 0) {
        setUploadStage(`Uploading screenshots (${screenshots.length})...`);
        const screenshotFormData = new FormData();
        screenshots.forEach(file => {
          screenshotFormData.append('screenshots', file);
        });

        const screenshotResponse = await fetch(`${process.env.REACT_APP_API_URL}/upload/screenshots`, {
          method: 'POST',
          body: screenshotFormData
        });

        if (screenshotResponse.ok) {
          const screenshotData = await screenshotResponse.json();
          
          await fetch(`${process.env.REACT_APP_API_URL}/upload/images`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              appId: result.data.id,
              images: screenshotData.data.map(s => ({ url: s.url, type: 'screenshot' }))
            })
          });
        }
      }

      setUploadProgress(100);
      setUploadStage('Upload complete!');
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reset form
      setFormData({
        app_name: '',
        package_name: '',
        version: '',
        version_code: '',
        description: '',
        long_description: '',
        category: 'Tools',
        size_mb: '',
        min_android_version: 'Android 6.0+',
        whats_new: ''
      });
      setApkFile(null);
      setIconFile(null);
      setScreenshots([]);
      screenshotPreviews.forEach(url => URL.revokeObjectURL(url));
      setScreenshotPreviews([]);
      setUploading(false);
      setUploadProgress(0);
      setUploadStage('');
      
      fetchApps();
      fetchStats();
      setCurrentView('apps');
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStage('Upload failed: ' + error.message);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setUploading(false);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0a0a0a] text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Upload Progress Modal */}
      {uploading && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-green-500/30 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-green-500/20">
            <div className="text-center mb-6">
              <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-2xl font-bold text-white mb-2">Uploading App</h3>
              <p className="text-gray-400 text-sm">{uploadStage}</p>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Progress</span>
                <span className="text-green-400 font-semibold">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-[#1a1a1a] rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500 ease-out rounded-full shadow-lg shadow-green-500/50"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>

            {uploadProgress === 100 && (
              <div className="mt-6 text-center">
                <div className="text-4xl mb-2">✅</div>
                <p className="text-green-400 font-semibold">Upload Successful!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Side Navigation */}
      <div className={`
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-64 bg-gradient-to-b from-[#111111] to-[#0a0a0a] border-r border-green-500/20 fixed h-full overflow-y-auto z-50 transition-transform duration-300 ease-in-out shadow-2xl
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-12 h-12 rounded-lg" />
              <div>
                <h1 className="text-lg font-black text-green-500">SisaTeck</h1>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="space-y-2">
            <button
              onClick={() => setCurrentView('dashboard')}
              className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-3 ${
                currentView === 'dashboard' 
                  ? 'bg-gradient-to-r from-green-500 to-green-400 text-black shadow-lg shadow-green-500/50' 
                  : 'text-gray-400 hover:bg-gradient-to-r hover:from-[#1a1a1a] hover:to-[#1f1f1f] hover:text-white hover:shadow-md'
              }`}
            >
              <span className="text-xl">📊</span>
              Dashboard
            </button>

            <button
              onClick={() => setCurrentView('apps')}
              className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-3 ${
                currentView === 'apps' 
                  ? 'bg-gradient-to-r from-green-500 to-green-400 text-black shadow-lg shadow-green-500/50' 
                  : 'text-gray-400 hover:bg-gradient-to-r hover:from-[#1a1a1a] hover:to-[#1f1f1f] hover:text-white hover:shadow-md'
              }`}
            >
              <span className="text-xl">📱</span>
              Apps
            </button>

            <button
              onClick={() => {
                setCurrentView('ratings');
                fetchAllRatings();
              }}
              className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-3 ${
                currentView === 'ratings' 
                  ? 'bg-gradient-to-r from-green-500 to-green-400 text-black shadow-lg shadow-green-500/50' 
                  : 'text-gray-400 hover:bg-gradient-to-r hover:from-[#1a1a1a] hover:to-[#1f1f1f] hover:text-white hover:shadow-md'
              }`}
            >
              <span className="text-xl">⭐</span>
              Ratings
            </button>

            <button
              onClick={() => setCurrentView('messages')}
              className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-3 relative ${
                currentView === 'messages' 
                  ? 'bg-gradient-to-r from-green-500 to-green-400 text-black shadow-lg shadow-green-500/50' 
                  : 'text-gray-400 hover:bg-gradient-to-r hover:from-[#1a1a1a] hover:to-[#1f1f1f] hover:text-white hover:shadow-md'
              }`}
            >
              <span className="text-xl">💬</span>
              Messages
              {unreadCount > 0 && (
                <span className="absolute right-4 top-3 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setCurrentView('settings')}
              className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-3 ${
                currentView === 'settings' 
                  ? 'bg-gradient-to-r from-green-500 to-green-400 text-black shadow-lg shadow-green-500/50' 
                  : 'text-gray-400 hover:bg-gradient-to-r hover:from-[#1a1a1a] hover:to-[#1f1f1f] hover:text-white hover:shadow-md'
              }`}
            >
              <span className="text-xl">⚙️</span>
              Settings
            </button>

            <button
              onClick={() => {
                setCurrentView('upload');
                setFormData({
                  app_name: '',
                  package_name: '',
                  version: '',
                  version_code: '',
                  description: '',
                  long_description: '',
                  category: 'Tools',
                  size_mb: '',
                  min_android_version: 'Android 6.0+',
                  whats_new: ''
                });
                setApkFile(null);
                setIconFile(null);
                setScreenshots([]);
                screenshotPreviews.forEach(url => URL.revokeObjectURL(url));
                setScreenshotPreviews([]);
              }}
              className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-3 ${
                currentView === 'upload' 
                  ? 'bg-gradient-to-r from-green-500 to-green-400 text-black shadow-lg shadow-green-500/50' 
                  : 'text-gray-400 hover:bg-gradient-to-r hover:from-[#1a1a1a] hover:to-[#1f1f1f] hover:text-white hover:shadow-md'
              }`}
            >
              <span className="text-xl">📤</span>
              Upload App
            </button>
          </nav>

          <div className="mt-8 pt-8 border-t border-[#2a2a2a]">
            <button
              onClick={handleLogout}
              className="w-full px-4 py-3 rounded-lg font-semibold bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-400 transition-all flex items-center gap-3"
            >
              <span className="text-xl">🚪</span>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 flex-1 flex flex-col">
        {/* Top Bar with Hamburger Menu */}
        <div className="lg:hidden bg-[#111111] border-b border-[#2a2a2a] p-4 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <div className="flex-1 p-4 md:p-8">
        {/* Dashboard View */}
        {currentView === 'dashboard' && stats && (
          <div>
            <div className="mb-8">
              <h2 className="text-4xl font-black bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">Dashboard</h2>
              <p className="text-gray-500">Overview of your app statistics</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/20 transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-green-400 text-sm font-semibold uppercase tracking-wide">Total Apps</div>
                  <div className="text-3xl">📱</div>
                </div>
                <div className="text-4xl font-black text-green-500">{stats.totalApps}</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-2xl p-6 hover:shadow-xl hover:shadow-blue-500/20 transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-blue-400 text-sm font-semibold uppercase tracking-wide">Downloads</div>
                  <div className="text-3xl">⬇️</div>
                </div>
                <div className="text-4xl font-black text-blue-400">{stats.totalDownloads?.toLocaleString()}</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 rounded-2xl p-6 hover:shadow-xl hover:shadow-purple-500/20 transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-purple-400 text-sm font-semibold uppercase tracking-wide">Ratings</div>
                  <div className="text-3xl">💬</div>
                </div>
                <div className="text-4xl font-black text-purple-400">{stats.totalRatings?.toLocaleString()}</div>
              </div>
              <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border border-yellow-500/20 rounded-2xl p-6 hover:shadow-xl hover:shadow-yellow-500/20 transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-yellow-400 text-sm font-semibold uppercase tracking-wide">Avg Rating</div>
                  <div className="text-3xl">⭐</div>
                </div>
                <div className="text-4xl font-black text-yellow-500">{stats.averageRating}</div>
              </div>
            </div>
          </div>
        )}

        {/* Apps List View */}
        {currentView === 'apps' && (
          <div>
            <div className="mb-8">
              <h2 className="text-4xl font-black bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">All Apps</h2>
              <p className="text-gray-500">Manage your published applications</p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {apps.map((app) => (
                <div key={app.id} className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#2a2a2a] hover:border-green-500/30 rounded-xl p-6 flex items-center justify-between hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300">
                  <div className="flex items-center gap-4 flex-1">
                    <img src={getImageUrl(app.icon_url)} alt={app.app_name} className="w-16 h-16 rounded-xl object-cover" />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-bold text-lg">{app.app_name}</h3>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                          app.status === 'approved' ? 'bg-green-500/20 text-green-400' : 
                          app.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {app.status || 'pending'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{app.package_name} • v{app.version}</p>
                      <div className="flex gap-4 mt-2 text-xs text-gray-400">
                        <span>⬇️ {app.total_downloads?.toLocaleString()} downloads</span>
                        <span>⭐ {app.rating} ({app.total_ratings} ratings)</span>
                        <span>📦 {app.size_mb} MB</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    {(!app.status || app.status === 'pending') && (
                      <>
                        <button
                          onClick={() => handleApproveApp(app.id)}
                          className="bg-gradient-to-r from-green-500/10 to-green-500/20 border border-green-500/30 hover:from-green-500/20 hover:to-green-500/30 text-green-400 font-semibold px-4 py-2 rounded-xl transition-all hover:shadow-lg hover:shadow-green-500/20 hover:scale-105"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => handleRejectApp(app.id)}
                          className="bg-gradient-to-r from-red-500/10 to-red-500/20 border border-red-500/30 hover:from-red-500/20 hover:to-red-500/30 text-red-400 font-semibold px-4 py-2 rounded-xl transition-all hover:shadow-lg hover:shadow-red-500/20 hover:scale-105"
                        >
                          ✗ Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleEditApp(app)}
                      className="bg-gradient-to-r from-blue-500/10 to-blue-500/20 border border-blue-500/30 hover:from-blue-500/20 hover:to-blue-500/30 text-blue-400 font-semibold px-4 py-2 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-500/20 hover:scale-105"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteApp(app.id)}
                      className="bg-gradient-to-r from-red-500/10 to-red-500/20 border border-red-500/30 hover:from-red-500/20 hover:to-red-500/30 text-red-400 font-semibold px-4 py-2 rounded-xl transition-all hover:shadow-lg hover:shadow-red-500/20 hover:scale-105"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ratings View */}
        {currentView === 'ratings' && (
          <div>
            <div className="mb-8">
              <h2 className="text-4xl font-black bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">Ratings & Reviews</h2>
              <p className="text-gray-500">Manage and respond to user feedback</p>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {ratings.length === 0 ? (
                <div className="text-center py-20 bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#2a2a2a] rounded-2xl">
                  <div className="text-6xl mb-4">⭐</div>
                  <p className="text-gray-400 text-lg">No ratings yet</p>
                  <p className="text-gray-600 text-sm mt-2">Ratings will appear here once users review your apps</p>
                </div>
              ) : (
                ratings.map((rating) => (
                  <div key={rating.id} className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#2a2a2a] hover:border-green-500/20 rounded-xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} className={`text-lg ${star <= rating.rating ? 'text-yellow-500' : 'text-gray-600'}`}>★</span>
                            ))}
                          </div>
                          <span className="text-sm text-gray-500">by {rating.user_name || 'Anonymous'}</span>
                          <span className="text-xs text-gray-600">• {new Date(rating.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="text-sm font-semibold text-green-400 mb-2">App: {rating.app_name}</div>
                        <p className="text-gray-300 text-sm mb-4">{rating.review || 'No review text'}</p>
                        
                        {rating.admin_reply ? (
                          <div className="bg-[#1a1a1a] border-l-4 border-green-500 rounded p-4">
                            <div className="text-xs text-gray-500 mb-1">
                              Admin Reply • {new Date(rating.admin_reply_at).toLocaleDateString()}
                            </div>
                            <p className="text-sm text-gray-300">{rating.admin_reply}</p>
                          </div>
                        ) : (
                          <div className="flex gap-3">
                            <input
                              type="text"
                              placeholder="Type your reply..."
                              id={`reply-${rating.id}`}
                              className="flex-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-500"
                            />
                            <button
                              onClick={() => {
                                const replyInput = document.getElementById(`reply-${rating.id}`);
                                if (replyInput.value.trim()) {
                                  handleReplyToRating(rating.id, replyInput.value.trim());
                                  replyInput.value = '';
                                }
                              }}
                              className="bg-green-500 hover:bg-green-400 text-black font-semibold px-6 py-2 rounded-lg transition-all"
                            >
                              Reply
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={() => handleDeleteRating(rating.id)}
                        className="ml-4 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-400 font-semibold px-4 py-2 rounded-lg transition-all"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Messages View */}
        {currentView === 'messages' && (
          <div>
            <div className="mb-8">
              <h2 className="text-4xl font-black bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">
                Contact Messages
              </h2>
              <p className="text-gray-500">View and manage contact form submissions</p>
            </div>

            {messages.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">💬</div>
                <p className="text-gray-500 text-lg">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`bg-[#1a1a1a] border rounded-2xl p-6 transition-all ${
                      msg.status === 'unread' ? 'border-green-500/40' : 'border-[#2a2a2a]'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-bold text-lg">{msg.name}</h3>
                          {msg.status === 'unread' && (
                            <span className="bg-green-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                              NEW
                            </span>
                          )}
                          {msg.status === 'replied' && (
                            <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                              REPLIED
                            </span>
                          )}
                        </div>
                        <div className="text-gray-400 text-sm space-y-1">
                          {msg.email && <div>📧 {msg.email}</div>}
                          {msg.phone && <div>📞 {msg.phone}</div>}
                          <div>📋 Subject: <span className="text-green-400">{msg.subject}</span></div>
                          <div>🕒 {new Date(msg.created_at).toLocaleString()}</div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {msg.status === 'unread' && (
                          <button
                            onClick={() => handleMarkAsRead(msg.id)}
                            className="bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                          >
                            Mark as Read
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-4">
                      <p className="text-gray-300 whitespace-pre-wrap">{msg.message}</p>
                    </div>

                    {msg.admin_reply && (
                      <div className="mt-4 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                        <div className="text-green-400 text-sm font-semibold mb-2">Admin Reply:</div>
                        <p className="text-gray-300">{msg.admin_reply}</p>
                        <div className="text-gray-500 text-xs mt-2">
                          Replied on: {new Date(msg.replied_at).toLocaleString()}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex gap-2">
                      <a
                        href={`https://wa.me/${msg.phone?.replace(/[^0-9]/g, '')}?text=Hi ${msg.name}, regarding your message about "${msg.subject}"`}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-green-500 hover:bg-green-400 text-black px-4 py-2 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-2"
                      >
                        💬 Reply via WhatsApp
                      </a>
                      {msg.email && (
                        <a
                          href={`mailto:${msg.email}?subject=Re: ${msg.subject}`}
                          className="bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-2"
                        >
                          📧 Reply via Email
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings View */}
        {currentView === 'settings' && (
          <div>
            <div className="mb-8">
              <h2 className="text-4xl font-black bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">App Settings</h2>
              <p className="text-gray-500">Configure your application preferences</p>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {settings.map((setting) => (
                <div key={setting.id} className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#2a2a2a] hover:border-green-500/20 rounded-xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <div>
                      <div className="text-sm font-semibold mb-1">{setting.setting_key}</div>
                      <div className="text-xs text-gray-500">{setting.description}</div>
                      <div className="text-xs text-gray-600 mt-1">Category: {setting.category}</div>
                    </div>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        defaultValue={setting.setting_value}
                        onBlur={(e) => {
                          if (e.target.value !== setting.setting_value) {
                            handleUpdateSetting(setting.setting_key, e.target.value);
                          }
                        }}
                        className="flex-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload App View */}
        {currentView === 'upload' && (
          <div>
            <div className="mb-8">
              <h2 className="text-4xl font-black bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">Upload New App</h2>
              <p className="text-gray-500">Add a new application to your portfolio</p>
            </div>
            <div className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-green-500/20 rounded-2xl p-8 shadow-2xl shadow-green-500/10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">App Name *</label>
                    <input
                      type="text"
                      name="app_name"
                      value={formData.app_name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Package Name *</label>
                    <input
                      type="text"
                      name="package_name"
                      value={formData.package_name}
                      onChange={handleInputChange}
                      required
                      placeholder="com.company.appname"
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Version *</label>
                    <input
                      type="text"
                      name="version"
                      value={formData.version}
                      onChange={handleInputChange}
                      required
                      placeholder="v1.0.0"
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Version Code *</label>
                    <input
                      type="number"
                      name="version_code"
                      value={formData.version_code}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    >
                      <option>Tools</option>
                      <option>Games</option>
                      <option>Productivity</option>
                      <option>Social</option>
                      <option>Entertainment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Size (MB) *</label>
                    <input
                      type="number"
                      step="0.1"
                      name="size_mb"
                      value={formData.size_mb}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Description *</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Long Description *</label>
                  <textarea
                    name="long_description"
                    value={formData.long_description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">What's New</label>
                  <textarea
                    name="whats_new"
                    value={formData.whats_new}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">APK File *</label>
                    <input
                      type="file"
                      accept=".apk"
                      onChange={(e) => setApkFile(e.target.files[0])}
                      required
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-500 file:text-black file:font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">App Icon *</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setIconFile(e.target.files[0])}
                      required
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-500 file:text-black file:font-semibold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Screenshots (Optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleScreenshotChange}
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-500 file:text-black file:font-semibold"
                  />
                  {screenshotPreviews.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-400 mb-2">Preview ({screenshotPreviews.length} screenshot{screenshotPreviews.length > 1 ? 's' : ''})</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {screenshotPreviews.map((preview, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={preview} 
                              alt={`Screenshot ${idx + 1}`} 
                              className="w-full h-32 object-cover rounded-lg border border-[#2a2a2a]"
                            />
                            <button
                              type="button"
                              onClick={() => removeScreenshot(idx)}
                              className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={uploading}
                  className={`w-full font-bold py-4 rounded-xl transition-all ${
                    uploading 
                      ? 'bg-gray-500 cursor-not-allowed' 
                      : 'bg-green-500 hover:bg-green-400 text-black hover:shadow-lg hover:shadow-green-500/50'
                  }`}
                >
                  {uploading ? 'Uploading...' : 'Upload App'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Edit App View */}
        {currentView === 'edit' && editingApp && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-4xl font-black bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">Edit App</h2>
                <p className="text-gray-500">{editingApp.app_name}</p>
              </div>
              <button
                onClick={() => {
                  setCurrentView('apps');
                  setEditingApp(null);
                }}
                className="px-6 py-3 bg-gradient-to-r from-[#2a2a2a] to-[#1f1f1f] hover:from-[#3a3a3a] hover:to-[#2f2f2f] rounded-xl transition-all font-semibold shadow-lg hover:shadow-xl"
              >
                ← Back to Apps
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-green-500/20 rounded-2xl p-8 shadow-2xl shadow-green-500/10">
              <form onSubmit={handleUpdateApp} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">App Name</label>
                    <input
                      type="text"
                      name="app_name"
                      value={formData.app_name}
                      onChange={handleInputChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Package Name</label>
                    <input
                      type="text"
                      name="package_name"
                      value={formData.package_name}
                      onChange={handleInputChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Version</label>
                    <input
                      type="text"
                      name="version"
                      value={formData.version}
                      onChange={handleInputChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Version Code</label>
                    <input
                      type="number"
                      name="version_code"
                      value={formData.version_code}
                      onChange={handleInputChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    >
                      <option>Tools</option>
                      <option>Games</option>
                      <option>Productivity</option>
                      <option>Social</option>
                      <option>Entertainment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Size (MB)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="size_mb"
                      value={formData.size_mb}
                      onChange={handleInputChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Long Description</label>
                  <textarea
                    name="long_description"
                    value={formData.long_description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">What's New</label>
                  <textarea
                    name="whats_new"
                    value={formData.whats_new}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 resize-none"
                  />
                </div>

                <div className="border-t border-[#2a2a2a] pt-6">
                  <h3 className="text-xl font-bold mb-4">Update Files (Optional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">New APK File</label>
                      <input
                        type="file"
                        accept=".apk"
                        onChange={(e) => setApkFile(e.target.files[0])}
                        className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-500 file:text-black file:font-semibold"
                      />
                      <p className="text-xs text-gray-500 mt-1">Current: {editingApp.apk_url}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">New App Icon</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setIconFile(e.target.files[0])}
                        className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-500 file:text-black file:font-semibold"
                      />
                      <div className="mt-2">
                        <img src={getImageUrl(editingApp.icon_url)} alt="Current icon" className="w-16 h-16 rounded-lg" />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-semibold mb-2">New Screenshots</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleScreenshotChange}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 focus:outline-none focus:border-green-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-500 file:text-black file:font-semibold"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to keep current screenshots</p>
                    {screenshotPreviews.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-400 mb-2">Preview ({screenshotPreviews.length} new screenshot{screenshotPreviews.length > 1 ? 's' : ''})</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {screenshotPreviews.map((preview, idx) => (
                            <div key={idx} className="relative group">
                              <img 
                                src={preview} 
                                alt={`Screenshot ${idx + 1}`} 
                                className="w-full h-32 object-cover rounded-lg border border-[#2a2a2a]"
                              />
                              <button
                                type="button"
                                onClick={() => removeScreenshot(idx)}
                                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-green-500 hover:bg-green-400 text-black font-bold py-4 rounded-xl transition-all"
                >
                  Save Changes
                </button>
              </form>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
